// base
import { createAsyncThunk } from '@reduxjs/toolkit';

// actions
import { riskDatabaseActions } from '../actions';

// apis
import { riskDatabaseApi } from '../apis';

import type {
  ReqRiskDatabaseActivity,
  ReqRiskDatabaseActivityBatch,
  ReqRiskDatabaseActivityUpdate,
  ReqRiskDatabaseHazard,
  ReqRiskDatabaseHazardBatch,
  ReqRiskDatabaseHazardDelete,
  ReqRiskDatabaseHazardUpdate,
  ReqRiskDatabaseWorks,
  ReqRiskDatabaseWorksBatch,
  ReqRiskDatabaseWorksUpdate,
  ResRiskDatabaseActivity,
  ResRiskDatabaseHazard,
  ResRiskDatabaseWorks
} from '../models';
import type { ResultError } from 'modules/common';

/** 위험 평가 설정 위험 데이터베이스 작업 조회 */
export const getRiskDatabaseWorks = createAsyncThunk<
  ResRiskDatabaseWorks,
  string,
  { rejectValue: ResultError }
>(
  riskDatabaseActions.GET_RISK_DATABASE_WORKS,
  async (disciplineId: string, { fulfillWithValue, rejectWithValue }) => {
    try {
      const res = await riskDatabaseApi.getRiskDatabaseWorks(disciplineId);

      return fulfillWithValue(res);
    } catch (error) {
      return rejectWithValue(error as ResultError);
    }
  }
);

/** 위험 평가 설정 위험 데이터베이스 작업 생성 */
export const addRiskDatabaseWorks = createAsyncThunk<
  ResRiskDatabaseWorks,
  ReqRiskDatabaseWorks,
  { rejectValue: ResultError }
>(
  riskDatabaseActions.ADD_RISK_DATABASE_WORKS,
  async (data: ReqRiskDatabaseWorks, { dispatch, fulfillWithValue, rejectWithValue }) => {
    try {
      await riskDatabaseApi.addRiskDatabaseWorks(data);

      const res = await dispatch(getRiskDatabaseWorks(data.disciplineId)).unwrap();

      return fulfillWithValue(res);
    } catch (error) {
      return rejectWithValue(error as ResultError);
    }
  }
);

/** 위험 평가 설정 위험 데이터베이스 작업 일괄 생성 */
export const addRiskDatabaseWorksBatch = createAsyncThunk<
  ResRiskDatabaseWorks,
  ReqRiskDatabaseWorksBatch,
  { rejectValue: ResultError }
>(
  riskDatabaseActions.ADD_RISK_DATABASE_WORKS_BATCH,
  async ({ disciplineId, data }, { dispatch, fulfillWithValue, rejectWithValue }) => {
    try {
      await riskDatabaseApi.addRiskDatabaseWorksBatch(data);

      const res = await dispatch(getRiskDatabaseWorks(disciplineId)).unwrap();

      return fulfillWithValue(res);
    } catch (error) {
      return rejectWithValue(error as ResultError);
    }
  }
);

/** 위험 평가 설정 위험 데이터베이스 작업 수정 */
export const editRiskDatabaseWorks = createAsyncThunk<
  ResRiskDatabaseWorks,
  ReqRiskDatabaseWorksUpdate,
  { rejectValue: ResultError }
>(
  riskDatabaseActions.UPDATE_RISK_DATABASE_WORKS,
  async ({ worksId, disciplineId, data }, { dispatch, fulfillWithValue, rejectWithValue }) => {
    try {
      await riskDatabaseApi.updateRiskDatabaseWorks(worksId, data);

      const res = await dispatch(getRiskDatabaseWorks(disciplineId)).unwrap();

      return fulfillWithValue(res);
    } catch (error) {
      return rejectWithValue(error as ResultError);
    }
  }
);

/** 위험 평가 설정 위험 데이터베이스 작업 삭제 */
export const deleteRiskDatabaseWorks = createAsyncThunk<
  ResRiskDatabaseWorks,
  { workId: number; disciplineId: string },
  { rejectValue: ResultError }
>(
  riskDatabaseActions.DELETE_RISK_DATABASE_WORKS,
  async ({ workId, disciplineId }, { dispatch, fulfillWithValue, rejectWithValue }) => {
    try {
      await riskDatabaseApi.deleteRiskDatabaseWorks(workId);

      const res = await dispatch(getRiskDatabaseWorks(disciplineId)).unwrap();

      return fulfillWithValue(res);
    } catch (error) {
      return rejectWithValue(error as ResultError);
    }
  }
);

/** 위험 평가 설정 위험 데이터베이스 활동 조회 */
export const getRiskDatabaseActivity = createAsyncThunk<
  ResRiskDatabaseActivity,
  number,
  { rejectValue: ResultError }
>(
  riskDatabaseActions.GET_RISK_DATABASE_ACTIVITY,
  async (worksId: number, { fulfillWithValue, rejectWithValue }) => {
    try {
      const res = await riskDatabaseApi.getRiskDatabaseActivity(worksId);

      return fulfillWithValue(res);
    } catch (error) {
      return rejectWithValue(error as ResultError);
    }
  }
);

/** 위험 평가 설정 위험 데이터베이스 활동 생성 */
export const addRiskDatabaseActivity = createAsyncThunk<
  ResRiskDatabaseActivity,
  ReqRiskDatabaseActivity,
  { rejectValue: ResultError }
>(
  riskDatabaseActions.ADD_RISK_DATABASE_ACTIVITY,
  async (data: ReqRiskDatabaseActivity, { dispatch, fulfillWithValue, rejectWithValue }) => {
    try {
      await riskDatabaseApi.addRiskDatabaseActivity(data);

      const res = await dispatch(getRiskDatabaseActivity(data.worksId)).unwrap();

      return fulfillWithValue(res);
    } catch (error) {
      return rejectWithValue(error as ResultError);
    }
  }
);

/** 위험 평가 설정 위험 데이터베이스 활동 일괄 생성 */
export const addRiskDatabaseActivityBatch = createAsyncThunk<
  ResRiskDatabaseActivity,
  ReqRiskDatabaseActivityBatch,
  { rejectValue: ResultError }
>(
  riskDatabaseActions.ADD_RISK_DATABASE_ACTIVITY_BATCH,
  async ({ worksId, data }, { dispatch, fulfillWithValue, rejectWithValue }) => {
    try {
      await riskDatabaseApi.addRiskDatabaseActivityBatch(data);

      const res = await dispatch(getRiskDatabaseActivity(worksId)).unwrap();

      return fulfillWithValue(res);
    } catch (error) {
      return rejectWithValue(error as ResultError);
    }
  }
);

/** 위험 평가 설정 위험 데이터베이스 활동 수정 */
export const editRiskDatabaseActivity = createAsyncThunk<
  ResRiskDatabaseActivity,
  ReqRiskDatabaseActivityUpdate,
  { rejectValue: ResultError }
>(
  riskDatabaseActions.UPDATE_RISK_DATABASE_ACTIVITY,
  async ({ activityId, data }, { dispatch, fulfillWithValue, rejectWithValue }) => {
    try {
      await riskDatabaseApi.updateRiskDatabaseActivity(activityId, data);

      const res = await dispatch(getRiskDatabaseActivity(data.worksId)).unwrap();

      return fulfillWithValue(res);
    } catch (error) {
      return rejectWithValue(error as ResultError);
    }
  }
);

/** 위험 평가 설정 위험 데이터베이스 활동 삭제 */
export const deleteRiskDatabaseActivity = createAsyncThunk<
  ResRiskDatabaseActivity,
  { worksId: number; activityId: number },
  { rejectValue: ResultError }
>(
  riskDatabaseActions.DELETE_RISK_DATABASE_ACTIVITY,
  async ({ worksId, activityId }, { dispatch, fulfillWithValue, rejectWithValue }) => {
    try {
      await riskDatabaseApi.deleteRiskDatabaseActivity(activityId);

      const res = await dispatch(getRiskDatabaseActivity(worksId)).unwrap();

      return fulfillWithValue(res);
    } catch (error) {
      return rejectWithValue(error as ResultError);
    }
  }
);

/** 위험 평가 설정 위험 데이터베이스 위험 조회 */
export const getRiskDatabaseHazard = createAsyncThunk<
  ResRiskDatabaseHazard,
  number,
  { rejectValue: ResultError }
>(
  riskDatabaseActions.GET_RISK_DATABASE_HAZARD,
  async (activityId, { fulfillWithValue, rejectWithValue }) => {
    try {
      const res = await riskDatabaseApi.getRiskDatabaseHazard(activityId);

      return fulfillWithValue(res);
    } catch (error) {
      return rejectWithValue(error as ResultError);
    }
  }
);

/** 위험 평가 설정 위험 데이터베이스 위험 생성 */
export const addRiskDatabaseHazard = createAsyncThunk<
  ResRiskDatabaseHazard,
  ReqRiskDatabaseHazard,
  { rejectValue: ResultError }
>(
  riskDatabaseActions.ADD_RISK_DATABASE_HAZARD,
  async (data, { dispatch, fulfillWithValue, rejectWithValue }) => {
    try {
      await riskDatabaseApi.addRiskDatabaseHazard(data);

      const res = await dispatch(getRiskDatabaseHazard(data.activityId)).unwrap();

      return fulfillWithValue(res);
    } catch (error) {
      return rejectWithValue(error as ResultError);
    }
  }
);

/** 위험 평가 설정 위험 데이터베이스 위험 일괄 생성 */
export const addRiskDatabaseHazardBatch = createAsyncThunk<
  ResRiskDatabaseHazard,
  ReqRiskDatabaseHazardBatch,
  { rejectValue: ResultError }
>(
  riskDatabaseActions.ADD_RISK_DATABASE_HAZARD_BATCH,
  async ({ activityId, data }, { dispatch, fulfillWithValue, rejectWithValue }) => {
    try {
      await riskDatabaseApi.addRiskDatabaseHazardBatch(data);

      const res = await dispatch(getRiskDatabaseHazard(activityId)).unwrap();

      return fulfillWithValue(res);
    } catch (error) {
      return rejectWithValue(error as ResultError);
    }
  }
);

/** 위험 평가 설정 위험 데이터베이스 위험 수정 */
export const editRiskDatabaseHazard = createAsyncThunk<
  ResRiskDatabaseHazard,
  ReqRiskDatabaseHazardUpdate,
  { rejectValue: ResultError }
>(
  riskDatabaseActions.UPDATE_RISK_DATABASE_HAZARD,
  async ({ hazardId, data }, { dispatch, fulfillWithValue, rejectWithValue }) => {
    try {
      await riskDatabaseApi.updateRiskDatabaseHazard(hazardId, data);

      const res = await dispatch(getRiskDatabaseHazard(data.activityId)).unwrap();

      return fulfillWithValue(res);
    } catch (error) {
      return rejectWithValue(error as ResultError);
    }
  }
);

/** 위험 평가 설정 위험 데이터베이스 위험 삭제 */
export const deleteRiskDatabaseHazard = createAsyncThunk<
  ResRiskDatabaseHazard,
  ReqRiskDatabaseHazardDelete,
  { rejectValue: ResultError }
>(
  riskDatabaseActions.DELETE_RISK_DATABASE_HAZARD,
  async ({ activityId, hazardId }, { dispatch, fulfillWithValue, rejectWithValue }) => {
    try {
      await riskDatabaseApi.deleteRiskDatabaseHazard(hazardId);

      const res = await dispatch(getRiskDatabaseHazard(activityId)).unwrap();

      return fulfillWithValue(res);
    } catch (error) {
      return rejectWithValue(error as ResultError);
    }
  }
);
